<template>
  <div id="national-president-list">
    <v-overlay :value="DeleteLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Premium Header Card -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <v-container class="pa-0">
          <v-row class="header-content" align="center">
            <!-- Left Section: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center gap-4">
              <div class="title-icon-wrapper">
                <v-icon size="48" color="white">mdi-crown</v-icon>
              </div>
              <div class="title-content">
                <h1 class="page-main-title">{{ PageTitle }}</h1>
                <p class="page-subtitle">
                  {{ YearName }} - {{ PageDescription }}
                </p>
              </div>
            </v-col>

            <!-- Right Section: Action Buttons -->
            <v-col
              cols="12"
              md="4"
              class="d-flex justify-end align-center action-buttons-group"
            >
              <v-btn
                class="modern-action-btn"
                @click.prevent="refreshPageData"
                icon
                color="white"
                v-tooltip="'Refresh Data'"
              >
                <v-icon size="28">mdi-refresh</v-icon>
              </v-btn>
              <router-link
                to="/national-president-flagship-application"
                style="text-decoration: none"
              >
                <v-btn
                  class="modern-action-btn"
                  icon
                  color="white"
                  v-tooltip="'Add New'"
                >
                  <v-icon size="28">mdi-plus-circle</v-icon>
                </v-btn>
              </router-link>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <v-dialog v-model="previewRecordPrompt" persistent max-width="75%">
      <events-preview
        pageTitle="National President Preview"
        pageDescription="National President Preview"
        :previewRecordPrompt="previewRecordPrompt"
        @hidePreviewRecordPrompt="hidePreviewRecordPrompt"
        :recordData="selectedData"
        v-if="previewRecordPrompt"
      ></events-preview>
    </v-dialog>

    <v-card-text class="page-content">
      <!-- Loading State -->
      <div class="d-flex justify-center mb-3" v-if="LoadingFlag">
        <v-progress-circular
          indeterminate
          color="#3a67b1"
          size="50"
        ></v-progress-circular>
      </div>

      <!-- No Records State -->
      <v-container class="py-0" v-if="tableData1.length == 0 && !LoadingFlag">
        <div class="empty-state">
          <v-icon size="80" color="#3a67b1" class="mb-4"
            >mdi-database-off</v-icon
          >
          <h3>No Records Found</h3>
          <p>There are no records available at this moment.</p>
        </div>
      </v-container>

      <!-- Data Available State -->
      <v-container class="py-0" v-if="tableData1.length > 0 && !LoadingFlag">
        <!-- Records Summary -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card class="status-card status-card--info" elevation="0">
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center gap-3">
                  <v-icon size="40" color="#0097d7"
                    >mdi-information-outline</v-icon
                  >
                  <div>
                    <div class="status-card-title">Total Records</div>
                    <div class="status-value">
                      {{ tableData1.length }} records found
                    </div>
                  </div>
                </div>
                <div class="d-flex gap-2">
                  <json-excel
                    v-if="
                      tableData1.length > 0 &&
                      tableOptions1.JsonDownloadButtonFlag == true
                    "
                    :name="tableOptions1.ExcelFileName"
                    :data="tableData1"
                    :fields="tableOptions1.JsonExcelFields"
                  >
                    <v-btn class="download-btn premium-btn" text small>
                      <v-icon left size="18">mdi-download</v-icon>
                      <span class="btn-text">Download</span>
                    </v-btn>
                  </json-excel>
                  <v-btn
                    class="download-btn"
                    text
                    small
                    v-if="ExcelDownloadFlag == true && ExcelFileFlag == false"
                    @click.prevent="generateReportExcel"
                    :loading="ExcelLoadingFlag"
                  >
                    <v-icon left size="18">mdi-file-excel</v-icon>
                    <span class="btn-text">Generate Report</span>
                  </v-btn>
                  <v-btn
                    class="download-btn premium-btn"
                    text
                    small
                    v-if="ExcelFileName != '' && ExcelFileFlag == true"
                    :href="ExcelFileName"
                    target="_blank"
                  >
                    <v-icon left size="18">mdi-download</v-icon>
                    <span class="btn-text">Download Report</span>
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Professional Data Table -->
        <v-card class="data-table-card" elevation="0">
          <div class="table-wrapper">
            <v-data-table
              class="professional-table"
              loading-text="Loading records... Please wait"
              v-model="selected"
              :search="search"
              :loading="TableLoadingFlag"
              :item-key="tableOptions1.ItemKey || 'id'"
              :single-select="tableOptions1.SingleSelectFlag || false"
              :headers="tableColumns1"
              :items="tableData1"
              :items-per-page="tableOptions1.ItemsPerPage || 10"
              :footer-props="tableOptions1.FooterProps || {}"
              :hide-default-footer="tableOptions1.HideDefaultFooter || false"
            >
              <template v-slot:item.ActiveStatusTxt="{ item }">
                <v-chip
                  :color="getActiveStatusColor(item.ActiveStatusTxt)"
                  dark
                  small
                  class="status-chip"
                >
                  <v-icon left size="16">mdi-check-circle</v-icon>
                  {{ item.ActiveStatusTxt }}
                </v-chip>
              </template>

              <template v-slot:item.ProfilePic="{ item }">
                <div class="profile-pic-wrapper">
                  <img
                    :src="item.ProfilePic"
                    alt="Profile"
                    class="profile-image"
                  />
                </div>
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="table-actions-group">
                  <v-tooltip bottom v-if="item.PreviewFlag == true">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="#0097d7"
                        @click="previewData(item)"
                        v-bind="attrs"
                        v-on="on"
                        class="action-icon-btn"
                      >
                        <v-icon size="20">mdi-file-search</v-icon>
                      </v-btn>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>

                  <v-tooltip bottom v-if="item.EditFlag">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="#3a67b1"
                        @click="editData(item)"
                        v-bind="attrs"
                        v-on="on"
                        class="action-icon-btn"
                      >
                        <v-icon size="20">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <v-tooltip bottom v-if="item.DeleteFlag">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="#e74c3c"
                        @click="deleteConfirmAlert(item)"
                        v-bind="attrs"
                        v-on="on"
                        class="action-icon-btn"
                      >
                        <v-icon size="20">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:loading>
                <div class="loading-state">
                  <v-progress-linear
                    indeterminate
                    color="#3a67b1"
                  ></v-progress-linear>
                </div>
              </template>

              <template v-slot:no-data>
                <div class="no-data-state">No data available</div>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </v-container>
    </v-card-text>

    <div class="page-footer"></div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import companyConfig from "@/company_config.json";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import Swal from "sweetalert2";
import EventsPreview from "@/view/pages/erp/events/EventsPreview.vue";

export default {
  mixins: [common],
  components: {
    EventsPreview,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      TableLoadingFlag: false,
      DeleteLoadingFlag: false,

      ExcelDownloadFlag: false,
      ExcelLoadingFlag: false,
      ExcelFileFlag: false,

      rows: [],

      Modules: {},

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      DownloadFlag: 0,
      ExcelFileName: "",
      excel_fields: {
        "Lom Id": "LomId",
        "Zone Id": "ZoneId",
        "Lom Name": "LomName",
        "President Name": "LomPresidentFullName",
        "LOM NAME": "LomName",
        "Mobile No": "LomPresidentMobileNo",
        "Email Id": "LomPresidentEmailid",
      },

      recordToEdit: "",
      selectedData: {},
      record: {},
      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      editPageDescription: "Assign LOM President for JCI India",

      selectedData: {},

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      ZoneCode: "",
      ZoneName: "",

      LomCode: "",
      LomName: "",

      YearCode: "",
      MonthCode: "",
      MonthName: "",

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        var YearName = tr.CurrentJciYearName;
        this.YearName = YearName;

        var MonthCode = this.$route.query.month;
        MonthCode = MonthCode == (null || undefined) ? 0 : MonthCode;
        console.log({ MonthCode });
        this.MonthCode = MonthCode;

        var tempDate1 = YearName + "-" + MonthCode + "-01";
        console.log({ tempDate1 });

        var tempDate2 = new Date(tempDate1);
        console.log({ tempDate2 });
        var MonthName = tempDate2.toLocaleString("en-us", { month: "long" });
        console.log({ MonthName });
        this.MonthName = MonthName;

        this.searchForm();
      }
    },
  },
  methods: {
    backPage() {
      this.$router.go(-1);
    },
    refreshPageData() {
      console.log("refreshPageData called");

      var ZoneName = this.$session.get("ZoneName");
      ZoneName = ZoneName == (null || undefined) ? "" : ZoneName;
      console.log("ZoneName=" + ZoneName);
      this.ZoneName = ZoneName;

      var ZoneCode = this.$session.get("ZoneId");
      ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
      console.log("ZoneCode=" + ZoneCode);
      this.ZoneCode = ZoneCode;

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? "" : LomCode;
      console.log("LomCode=" + LomCode);
      this.LomCode = LomCode;

      var LomName = this.$session.get("LomName");
      LomName = LomName == (null || undefined) ? "" : LomName;
      console.log("LomName=" + LomName);
      this.LomName = LomName;

      this.LoadingFlag = true;

      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "national_president",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/lom/create";
      var params = {};
      this.pageRedirect(params, url);
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var url = "/national-president-flagship-edit";
        var LomCode = this.LomCode;
        var JciEventId = tr.JciEventId;
        console.log({ LomCode });
        console.log({ JciEventId });
        var params = {
          v1: LomCode,
          v2: JciEventId,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one event to edit";
        this.toast("error", message);
      }
    },
    resetForm() {
      this.$refs.form2.reset();
      this.tableData1 = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.searchForm();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.searchForm();
    },
    editAlert(tr) {
      console.log("editAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var LomId = tr.LomId;
        console.log("LomId=" + LomId);
        this.record = tr;
        this.recordToEdit = LomId;
        this.editPageDescription =
          "Assign 2021 LOM President for " + tr.LomName;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to modifiy";
        this.toast("error", message);
      }
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    searchForm() {
      console.log("searchForm is called");

      var message = "";

      // var validate1 = this.$refs.form2.validate();
      // console.log("validate1=" + validate1);

      var YearCode = this.JciYearCode;
      console.log({ YearCode });

      var LomCode = this.LomCode;
      console.log({ LomCode });

      if (YearCode != "" && LomCode != "") {
        this.LoadingFlag = true;
        this.tableData1 = [];
        this.ExcelDownloadFlag = false;
        this.ExcelFileName = "";
        this.ExcelFileFlag = false;
        this.Modules = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/lists";
        var upload = {
          UserInterface: 7,
          Year: this.JciYearCode,
          Lom: this.LomCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var table = {};
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            console.log({ records });

            if (flag == 1) {
              var options = records.TableOptions;
              console.log({ options });

              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableOptions1 = options;
              thisIns.ExcelDownloadFlag = options.ExcelDownloadButtonFlag;
              thisIns.tableData1 = records.TableData;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (LomCode == "") {
          message += "Lom should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    generateReportExcel() {
      console.log("generateReportExcel is called");

      // var validate1 = this.$refs.form2.validate();
      var FromDate = "2021-08-04";
      var ToDate = "2021-08-05";
      var validate1 = true;
      console.log(
        "validate1=" +
          validate1 +
          ", FromDate=" +
          FromDate +
          ", ToDate=" +
          ToDate
      );

      if (validate1) {
        this.ExcelFileName = "";
        this.ExcelLoadingFlag = true;
        this.ExcelFileFlag = false;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          server_url + "api/reports/directory-information/generate-excel";
        var upload = {
          UserInterface: 1,
          YearCode: this.JciYearCode,
          ZoneCode: this.ZoneCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()

            thisIns.ExcelLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.ExcelFileName = records.ExcelFileName;
              thisIns.ExcelDownloadFlag = records.ExcelDownloadFlag;
              thisIns.ExcelFileFlag = true;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.ExcelLoadingFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/> Event Type <b> " + tr.OrganizationTypeName + "</b>";
        htmlTxt += "<br/> Portfolio <b> " + tr.PortfolioShortCode + "</b>";
        htmlTxt += "<br/> Event name <b> " + tr.JciEventName + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr.JciEventId);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(EventId) {
      console.log("deleteData is called");

      console.log("EventId=" + EventId);

      if (EventId > 0) {
        this.DeleteLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/destroy";
        var upload = {
          UserInterface: 1,
          EventId: EventId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.DeleteLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.DeleteLoadingFlag = false;
          });
      } else {
        var message = "Event Id should not be empty. ";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Color Palette (JCI Approved Colors)
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;

$primary-color: $navy;
$secondary-color: $aqua;
$success-color: $seaform;
$warning-color: $gold;
$danger-color: #e74c3c;
$info-color: $aqua;
$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$text-primary: $text-off-black;
$text-secondary: $text-90-black;
$border-color: #e2e8f0;
$gradient-main: linear-gradient(135deg, $navy 0%, $aqua 100%);

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

#national-president-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 32px 16px;
  position: relative;

  .title-icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
    padding: 16px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(12px);
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: all 0.4s ease;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-4px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
    }
  }

  .title-content {
    animation: slideInLeft 0.8s ease-out 0.1s backwards;
  }

  .page-main-title {
    font-size: 42px;
    font-weight: 900;
    color: white;
    margin: 0;
    letter-spacing: 1.2px;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
    line-height: 1.1;
  }

  .page-subtitle {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.95);
    margin: 8px 0 0 0;
    opacity: 0.95;
    letter-spacing: 0.3px;
    font-weight: 500;
  }

  .action-buttons-group {
    display: flex;
    gap: 12px;
    animation: slideInRight 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);

    .modern-action-btn {
      background: rgba(255, 255, 255, 0.15) !important;
      border: 2px solid rgba(255, 255, 255, 0.3) !important;
      backdrop-filter: blur(10px);
      border-radius: 12px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);

      &:hover {
        background: rgba(255, 255, 255, 0.25) !important;
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
      }

      &:active {
        transform: translateY(-2px);
      }
    }
  }

  // Header Card
  .header-card {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.2);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
        circle at 20% 50%,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 50%
      );
      pointer-events: none;
    }

    .header-gradient {
      position: relative;
      z-index: 2;
      padding: 32px 24px;
    }

    .header-content {
      position: relative;
      z-index: 3;
    }
  }

  // Status Cards
  .status-card {
    border-radius: 18px !important;
    border: none !important;
    transition: all 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    animation: fadeInScale 0.7s ease-out;
    position: relative;
    overflow: hidden;
    padding: 24px !important;
    background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%) !important;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
        circle at 100% 0%,
        rgba(0, 151, 215, 0.08) 0%,
        transparent 60%
      );
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 20px 50px rgba(58, 103, 177, 0.2);
    }

    &--info {
      border: 2px solid rgba(0, 151, 215, 0.2) !important;
      box-shadow: 0 12px 40px rgba(0, 151, 215, 0.15);
    }

    .status-card-title {
      font-size: 13px;
      font-weight: 700;
      color: $text-secondary;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin: 0;
    }

    .status-value {
      font-size: 24px;
      font-weight: 900;
      color: #2563eb;
      margin: 8px 0 0 0;
      font-family: "Courier New", monospace;
    }
  }

  // Empty State
  .empty-state {
    text-align: center;
    padding: 80px 32px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 18px;
    border: 2px dashed rgba(58, 103, 177, 0.2);
    animation: fadeInScale 0.7s ease-out;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
        circle at 50% 0%,
        rgba(0, 151, 215, 0.05) 0%,
        transparent 50%
      );
      pointer-events: none;
    }

    h3 {
      font-size: 24px;
      font-weight: 700;
      color: $text-primary;
      margin: 16px 0 8px 0;
      letter-spacing: 0.5px;
    }

    p {
      font-size: 14px;
      color: $text-secondary;
      margin: 0;
      letter-spacing: 0.3px;
    }
  }

  // Table Styling
  .table-wrapper {
    overflow-x: auto;
    border-radius: 18px;
    background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
    padding: 0;
    border: 2px solid rgba(58, 103, 177, 0.12);
    animation: fadeInScale 0.8s ease-out;
    position: relative;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.25),
      0 0 36px rgba(0, 151, 215, 0.12);

    &:hover {
      box-shadow: 0 20px 56px rgba(58, 103, 177, 0.3),
        0 0 40px rgba(0, 151, 215, 0.15);
    }

    .professional-table {
      background: transparent !important;

      thead tr {
        background: linear-gradient(90deg, #3a67b1 0%, #0097d7 100%) !important;
        border-bottom: 3px solid rgba(58, 103, 177, 0.2) !important;

        th {
          color: white !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          padding: 16px 12px !important;
          text-align: center !important;
          background: transparent !important;
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid rgba(58, 103, 177, 0.1) !important;
          transition: all 0.3s ease;

          &:nth-of-type(odd) {
            background-color: rgba(58, 103, 177, 0.03) !important;
          }

          &:hover {
            background-color: rgba(0, 151, 215, 0.08) !important;
            box-shadow: inset 4px 0 0 #0097d7;
          }

          td {
            padding: 14px 12px !important;
            color: $text-primary !important;
            font-size: 13px !important;
            text-align: center !important;
          }
        }
      }
    }
  }

  .data-table-card {
    border-radius: 18px !important;
    border: 2px solid rgba(58, 103, 177, 0.15) !important;
    box-shadow: 0 16px 56px rgba(58, 103, 177, 0.2),
      0 0 36px rgba(0, 151, 215, 0.12), inset 0 1px 1px rgba(255, 255, 255, 0.6) !important;
    transition: all 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    animation: fadeInScale 0.75s ease-out;
    background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%) !important;
    position: relative;
    overflow: visible;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 18px;
      background: radial-gradient(
        circle at 100% 0%,
        rgba(0, 151, 215, 0.1) 0%,
        transparent 70%
      );
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 24px 64px rgba(58, 103, 177, 0.3),
        0 0 40px rgba(0, 151, 215, 0.2),
        inset 0 1px 1px rgba(255, 255, 255, 0.8) !important;
    }
  }

  .profile-pic-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;

    .profile-image {
      width: 80px;
      height: 80px;
      border-radius: 12px;
      border: 2px solid rgba(58, 103, 177, 0.2);
      object-fit: cover;
      transition: all 0.3s ease;

      &:hover {
        border-color: #0097d7;
        transform: scale(1.05);
        box-shadow: 0 8px 24px rgba(0, 151, 215, 0.3);
      }
    }
  }

  .table-actions-group {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;

    .action-icon-btn {
      transition: all 0.3s ease;
      border-radius: 8px;
      padding: 6px !important;

      &:hover {
        transform: scale(1.15);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  .status-chip {
    transition: all 0.3s ease;
    backdrop-filter: blur(8px);
    font-weight: 600;
    letter-spacing: 0.3px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    }

    .v-icon {
      margin-right: 4px !important;
    }
  }

  .download-btn {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    border-radius: 12px;
    padding: 8px 16px !important;
    font-size: 12px;
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    color: white !important;
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
    display: inline-flex;
    align-items: center;
    gap: 8px;

    &.premium-btn {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
      box-shadow: 0 8px 24px rgba(245, 158, 11, 0.3);
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      transition: left 0.6s ease;
    }

    .btn-text {
      position: relative;
      z-index: 1;
    }

    .v-icon {
      position: relative;
      z-index: 1;
    }

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(16, 185, 129, 0.4);

      &::before {
        left: 100%;
      }

      &.premium-btn {
        box-shadow: 0 12px 32px rgba(245, 158, 11, 0.4);
      }
    }

    &:active {
      transform: translateY(-2px);
    }
  }

  // Page Content & Footer
  .page-content {
    padding: 0;
    position: relative;
    z-index: 2;
  }

  .page-footer {
    height: 24px;
  }

  // Loading State
  .loading-state {
    padding: 24px;
    text-align: center;
  }

  .no-data-state {
    padding: 24px;
    text-align: center;
    color: $text-secondary;
    font-weight: 500;
  }

  // Responsive Design
  @media (max-width: 1200px) {
    padding: 24px 12px;

    .page-main-title {
      font-size: 32px;
    }

    .page-subtitle {
      font-size: 14px;
    }

    .title-icon-wrapper {
      padding: 12px;
    }

    .data-table-card {
      border-radius: 16px !important;
    }
  }

  @media (max-width: 960px) {
    padding: 20px 10px;

    .page-main-title {
      font-size: 28px;
    }

    .action-buttons-group {
      gap: 8px;
      margin-top: 16px;
    }

    .data-table-card {
      border-radius: 14px !important;
    }
  }

  @media (max-width: 768px) {
    padding: 16px 8px;

    .page-main-title {
      font-size: 24px;
    }

    .page-subtitle {
      font-size: 13px;
    }

    .title-icon-wrapper {
      padding: 10px;
    }

    .action-buttons-group {
      gap: 6px;

      .modern-action-btn {
        padding: 6px !important;
      }
    }

    .data-table-card {
      border-radius: 12px !important;
    }

    .table-wrapper {
      border-radius: 12px;
    }

    .status-card {
      padding: 16px !important;

      .status-value {
        font-size: 18px;
      }
    }

    .empty-state {
      padding: 40px 16px;

      h3 {
        font-size: 18px;
      }
    }
  }

  @media (max-width: 600px) {
    padding: 12px 6px;

    .page-main-title {
      font-size: 20px;
      letter-spacing: 0.5px;
    }

    .title-icon-wrapper {
      padding: 8px;
      margin-right: 8px;
    }

    .action-buttons-group {
      gap: 4px;
      flex-wrap: wrap;

      .modern-action-btn {
        padding: 4px !important;
      }
    }

    .status-card {
      padding: 12px !important;
      flex-direction: column;

      .status-value {
        font-size: 16px;
      }
    }

    .download-btn {
      padding: 6px 12px !important;
      font-size: 11px;
    }
  }

  @media (max-width: 480px) {
    padding: 8px 4px;

    .page-main-title {
      font-size: 18px;
    }

    .title-icon-wrapper {
      padding: 6px;
    }

    .action-buttons-group {
      gap: 2px;
    }

    .data-table-card {
      border-radius: 10px !important;
    }

    .table-wrapper {
      border-radius: 10px;
    }
  }
}

// Utility classes
.gap-4 {
  gap: 16px;
}

.d-flex {
  display: flex;
}

.justify-end {
  justify-content: flex-end;
}

.justify-center {
  justify-content: center;
}

.align-center {
  align-items: center;
}

.overflow-hidden {
  overflow: hidden;
}
</style>